import { useCallback, useMemo, useState } from "react";

import {
    isLendVaultComingSoon,
    LendVaultSortType,
    UserVaultPositionStatus,
    useLendVaults,
    UserVaultPositionExpanded,
    FetchLendVaultsInfoParams,
    StakeSortType,
    UserLendingVaultFilter,
    VaultMetadataTag,
    useLoopscaleWalletContext
} from "@bridgesplit/abf-react";
import { useLocalStorage } from "@bridgesplit/react";
import { combineCollections, removeDuplicates, SortDirection, SortOrder } from "@bridgesplit/utils";
import { PaginationController } from "@bridgesplit/ui";

import { getPrincipalMints, useChunkedPaginationController } from "./shared";

const DEFAULT_SORT_SIDE = SortDirection.Desc;
const DEFAULT_SORT_TYPE = LendVaultSortType.TotalDeposits;
const DEFAULT_STAKE_SORT_TYPE = StakeSortType.WAvgApy;

export type LendingVaultPagination = ReturnType<typeof useLendingVaultPagination>;

export function useLendingVaultPagination() {
    const [sortDirection, setSortDirection] = useLocalStorage<SortDirection>("LendVaultsOrder", DEFAULT_SORT_SIDE);
    const [sortType, setSortType] = useLocalStorage<LendVaultSortType>("LendVaultsSort", DEFAULT_SORT_TYPE);
    const [page, setPage] = useState<number>(0);
    const [principalMints, setPrincipalMints] = useState<string[]>([]);

    const tableController: PaginationController = {
        page,
        setPage,
        order: sortDirection === SortDirection.Asc ? SortOrder.Asc : SortOrder.Desc,
        setOrder: (order) => setSortDirection(order === SortOrder.Asc ? SortDirection.Asc : SortDirection.Desc),
        orderBy: sortType.toString(),
        setOrderBy: (orderBy) => {
            const order = parseInt(orderBy);
            if (!Object.values(LendVaultSortType).includes(order)) return;

            setSortType(order as LendVaultSortType);
        },
        resetPage: () => setPage(0)
    };

    const params: FetchLendVaultsInfoParams = {
        principalMints,
        sortType,
        sortDirection,
        page,
        pageSize: 50
    };

    const reset = useCallback(() => {
        setSortDirection(DEFAULT_SORT_SIDE);
        setSortType(DEFAULT_SORT_TYPE);
        setPage(0);
    }, [setSortDirection, setSortType, setPage]);

    return {
        page,
        params,
        tableController,
        reset,
        principalMints,
        setPrincipalMints,
        setSortDirection,
        setSortType
    };
}

export function useFilteredLendVaults() {
    const pagination = useLendingVaultPagination();
    const { data, totalDataCount, isFetching, strategiesLoading, strategiesFetching } = useLendVaults({
        // depositsEnabled filter is applied client-side below so this filter shape matches
        // useFarmingInfos's useLendVaults call → both share one lendingVaultInfos cache entry.
        filter: pagination.params,
        skipTimelocks: true
    });

    // Strategy loading is decoupled inside useLendVaults, but every card stat (APY, TVL, deposits) is
    // strategy-derived, so hold the list in its skeleton state until the summaries resolve rather than
    // briefly rendering zeroes. Gate on first-load only (strategiesLoading) so background refetches don't
    // blink the featured cards back to skeletons.
    const vaults = strategiesLoading ? undefined : data;

    const filteredVaults = useMemo(() => {
        if (!vaults) return { genesis: undefined, lendVaults: undefined };

        // Coming-soon vaults bypass depositsEnabled (no on-chain state yet) and UrlAccessOnly
        const visibleVaults = vaults.filter(
            (v) =>
                isLendVaultComingSoon(v) ||
                (v?.vault?.depositsEnabled && !v?.vaultMetadata?.tags?.includes(VaultMetadataTag.UrlAccessOnly))
        );
        const featuredVaults = visibleVaults.filter((v) => v?.vaultMetadata?.tags?.includes(VaultMetadataTag.Featured));
        const nonFeaturedVaults = visibleVaults.filter(
            (v) => !v?.vaultMetadata?.tags?.includes(VaultMetadataTag.Featured)
        );
        const lendVaults = removeDuplicates(combineCollections([featuredVaults, nonFeaturedVaults]));

        return {
            featuredVaults,
            lendVaults
        };
    }, [vaults]);

    return { filteredVaults, totalDataCount, isFetching: isFetching || strategiesFetching, pagination };
}

const DEFAULT_PAGE_SIZE = 5;

export function useLendingPositionPagination() {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    const [status, setStatus] = useState<UserVaultPositionStatus>(UserVaultPositionStatus.Active);
    const [sortDirection, setSortDirection] = useLocalStorage<SortDirection>("LendVaultsStakeOrder", DEFAULT_SORT_SIDE);
    const [sortType, setSortType] = useLocalStorage<StakeSortType>("LendVaultsStakeSort", DEFAULT_STAKE_SORT_TYPE);
    const [principalMint, setPrincipalMint] = useState<string | undefined>(undefined);
    const [vaultAddresses, setVaultAddresses] = useState<string[]>([]);
    const [lpMints, setLpMints] = useState<string[]>([]);
    const [depositsEnabled, setDepositsEnabled] = useState<boolean | undefined>(undefined);

    const chunkedPagination = useChunkedPaginationController<UserVaultPositionExpanded, UserLendingVaultFilter>(
        {
            userAddress: activeWallet ?? "",
            vaultAddresses,
            principalMints: getPrincipalMints(principalMint),
            lpMints,
            depositsEnabled,
            sort: sortType,
            sortDirection,
            status
        },
        DEFAULT_PAGE_SIZE
    );

    const reset = useCallback(() => {
        setStatus(UserVaultPositionStatus.Active);
        setSortDirection(DEFAULT_SORT_SIDE);
        setSortType(DEFAULT_STAKE_SORT_TYPE);
        setVaultAddresses([]);
        setLpMints([]);
        setDepositsEnabled(undefined);
        chunkedPagination.resetPagination();
    }, [
        setSortDirection,
        setSortType,
        setStatus,
        chunkedPagination,
        setVaultAddresses,
        setLpMints,
        setDepositsEnabled
    ]);

    return {
        ...chunkedPagination,
        params: chunkedPagination.clientPagination,
        reset,
        principalMint,
        setStatus,
        setPrincipalMint,
        setSortDirection,
        setSortType,
        setVaultAddresses,
        setLpMints,
        setDepositsEnabled
    };
}
